import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { citiesData } from '../../../../../redux/features/dashboard/dashboardSelectors';
import {
  useAddCityMutation,
  useDeleteCityMutation,
  useGetCityQuery,
  useUpdateCityMutation,
} from '../../../../../redux/services/data/citiesApi';

import { FormTemplate } from '../../FormTemplate';
import { InputsTemplate } from '../../../inputs/InputsTemplate';
import { InputList } from '../../../inputs/InputList';
import { Loader } from '../../../../ui/Loader';
import { DeleteDialogButton } from '../../../DeleteDialogButton';

import {
  initialValues,
  mock,
  validationSchemaCity,
} from '../../../../../lib/mocks/add-city-mock';
import { handleResponseError } from '../../../../../utils/data';
import {
  errorMessages,
  selectsLocationNames,
} from '../../../../../data/constants';
import { closeDialog, showError } from '../../../../../utils/ui';

export const AddCityForm = ({
  formProps = {},
  name = {},
  isEdit = false,
  handleClose = () => {},
  handleResetInput = () => {},
}) => {
  const [error, setError] = useState('');

  const userToken = useSelector(selectUserToken);
  const cities = useSelector(citiesData);

  const cityId = useMemo(
    () => formProps?.values?.[name],
    [formProps?.values, name]
  );

  const {
    data: city,
    isFetching: isCityLoading,
    error: cityError,
  } = useGetCityQuery(
    { token: userToken, id: cityId },
    { skip: !isEdit || !cityId || !userToken }
  );

  const [addNewCity, { isLoading: isAddingLoading, error: addingError }] =
    useAddCityMutation();
  const [editCity, { isLoading: isEditingLoading, error: editingError }] =
    useUpdateCityMutation();
  const [deleteCity, { isLoading: isDeletingLoading, error: deletingError }] =
    useDeleteCityMutation();

  const isLoading =
    (isEdit && isCityLoading) ||
    isAddingLoading ||
    isEditingLoading ||
    isDeletingLoading;

  const formInitialValues = useMemo(() => {
    if (isEdit && city) {
      return {
        name: city.name || '',
        postal_code: city.postal_code || '',
        latitude: city.latitude ?? '',
        longitude: city.longitude ?? '',
        city_neighbors: city.city_neighbors || '',
      };
    }
    return { ...initialValues };
  }, [isEdit, city]);

  const coordinateFields = useMemo(() => mock.filter(item => item.listId), []);

  const neighborDefaultValue = useMemo(() => {
    if (!isEdit || !city?.city_neighbors?.length) return [];
    return city.city_neighbors.map(n => ({
      id: n.id,
      value: n.id,
      title: n.name,
    }));
  }, [isEdit, city]);

  const regularFields = useMemo(
    () =>
      mock
        .filter(item => !item.listId)
        .map(item =>
          item.name === 'city_neighbors'
            ? {
                ...item,
                selectData:
                  cities?.filter(c => c.id.toString() !== cityId?.toString()) ?? [],
                defaultValue: neighborDefaultValue,
              }
            : item
        ),
    [cities, cityId, neighborDefaultValue]
  );

  const handleDelete = async setIsDeleteClick => {
    setIsDeleteClick(false);
    setError('');

    const response = await deleteCity({
      token: userToken,
      id: cityId,
    });

    const errorStatus = handleResponseError(
      response?.error,
      deletingError,
      () => closeDialog({ handleClose })
    );

    if (!errorStatus) {
      handleResetInput();
    }

    setError(errorStatus);
  };

  const handleSubmit = async values => {
    setError(null);

    const newValues = {
      ...values,
      country_id: formProps?.values[selectsLocationNames.country],
      city_neighbors: values.city_neighbors || [],
    };

    let response;
    let errorStatus;

    if (isEdit) {
      response = await editCity({
        token: userToken,
        id: cityId,
        data: newValues,
      });

      errorStatus = handleResponseError(response?.error, editingError, () =>
        closeDialog({ handleClose })
      );
    } else {
      response = await addNewCity({ token: userToken, data: newValues });

      errorStatus = handleResponseError(response?.error, addingError, () =>
        closeDialog({ handleClose })
      );
    }

    setError(errorStatus);
  };

  useEffect(() => {
    if (cityError) {
      setError(errorMessages.wentWrong);
    }
  }, [cityError]);

  useEffect(() => {
    showError(error);
  }, [error]);

  return (
    <div className="relative flex flex-col gap-4 w-[260px] sm:w-[420px] exclude-click">
      {!isLoading && (
        <FormTemplate
          initialValues={formInitialValues}
          validationSchema={Yup.object(validationSchemaCity)}
          handleSubmitForm={handleSubmit}
          buttonText={'Save'}
          bgColor={'bg-purpleColor border-purpleColor'}
        >
          {formProps => (
            <>
              <InputsTemplate
                formProps={formProps}
                inputsList={regularFields}
              />

              <InputList
                formProps={formProps}
                inputs={coordinateFields}
              />

              {isEdit && (
                <DeleteDialogButton
                  isDeletingLoading={isDeletingLoading}
                  handleDelete={handleDelete}
                  error={error}
                />
              )}
            </>
          )}
        </FormTemplate>
      )}

      {isLoading && (
        <div className="flex items-center justify-center w-full h-[326px]">
          <Loader />
        </div>
      )}
    </div>
  );
};
